<template>
  <div class="footer-offset renedian-light-sand--background">
    
    <!-- Loading State -->
    <div v-if="loading" class="d-flex align-center justify-center" style="min-height: 400px;">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    
    <!-- Page Content (only show header and content when page exists) -->
    <template v-else-if="page">
      <RippedHeader :breadcrumb="page.breadcrumb || ''" :title="page.title || ''" />
    
      <!-- Static Page Rendering -->
      <div class="d-flex flex-column align-center" style="margin-top: 100px;">
        <div class="width-90-vw barlow-regular page-content">

        <div v-if="(page.blocks || []).length">
          <div v-for="block in page.blocks" :key="block.id" class="mb-8">
            <!-- Text -->
            <div v-if="block.type === 'text'">
              <div v-html="block.text"></div>
            </div>

            <!-- Full-width Image -->
            <div v-else-if="block.type === 'image_full'" class="d-flex flex-column align-center">
              <img :src="$img(block.imageUrl, 1200)" :alt="block.alt || ''" class="mb-2" loading="lazy" />
              <div v-if="block.caption" class="caption grey--text text--darken-1">{{ block.caption }}</div>
            </div>

            <!-- Image Left / Text Right -->
            <div v-else-if="block.type === 'image_left_text_right'">
              <v-row align="stretch" class="equal-height-row">
                <v-col cols="12" md="5" class="image-col">
                  <div class="image-wrapper">
                    <img :src="$img(block.imageUrl, 800)" :alt="block.alt || ''" class="block-image" :style="getImageStyle(block.id)" loading="lazy" @load="onBlockImageLoad" />
                  </div>
                  <div v-if="block.caption" class="caption grey--text text--darken-1 text-center">{{ block.caption }}</div>
                </v-col>
                <v-col :ref="'textCol_' + block.id" cols="12" md="7">
                  <div v-html="block.text"></div>
                </v-col>
              </v-row>
            </div>

            <!-- Text Left / Image Right -->
            <div v-else-if="block.type === 'image_right_text_left'">
              <v-row align="stretch" class="equal-height-row">
                <v-col :ref="'textCol_' + block.id" cols="12" md="7">
                  <div v-html="block.text"></div>
                </v-col>
                <v-col cols="12" md="5" class="image-col">
                  <div class="image-wrapper">
                    <img :src="$img(block.imageUrl, 800)" :alt="block.alt || ''" class="block-image" :style="getImageStyle(block.id)" loading="lazy" @load="onBlockImageLoad" />
                  </div>
                  <div v-if="block.caption" class="caption grey--text text--darken-1 text-center">{{ block.caption }}</div>
                </v-col>
              </v-row>
            </div>

            <!-- Three Images -->
            <div v-else-if="block.type === 'images_three'">
              <v-row>
                <v-col cols="12" md="4" v-for="(img, idx) in (block.images || [])" :key="idx">
                  <img :src="$img(img.url, 600)" :alt="img.alt || ''" class="mb-2 width-100" loading="lazy" />
                  <div v-if="img.caption" class="caption grey--text text--darken-1 text-center">{{ img.caption }}</div>
                </v-col>
              </v-row>
            </div>

            <!-- Two Images -->
            <div v-else-if="block.type === 'images_two'">
              <v-row>
                <v-col cols="12" md="6" v-for="(img, idx) in (block.images || [])" :key="idx">
                  <img :src="$img(img.url, 800)" :alt="img.alt || ''" class="mb-2" loading="lazy" />
                  <div v-if="img.caption" class="caption grey--text text--darken-1 text-center">{{ img.caption }}</div>
                </v-col>
              </v-row>
            </div>

            <!-- Two Column Text -->
            <div v-else-if="block.type === 'text_two_columns'">
              <v-row>
                <v-col cols="12" md="6"><div v-html="block.leftText"></div></v-col>
                <v-col cols="12" md="6"><div v-html="block.rightText"></div></v-col>
              </v-row>
            </div>
          </div>
        </div>
        </div>
      </div>
    </template>
    
  </div>
</template>

<script>
import { getFirestore, collection, query, where, limit, getDocs, doc, setDoc, serverTimestamp, increment } from 'firebase/firestore'
import { metaMixin } from '@/mixins/metaMixin'
import RippedHeader from '@/components/RippedHeader.vue'
import { normalizeRedirectPath, pathVariants, encodeMissingPathId, monthKey } from '@/utils/redirects'
import { emitPrerenderReady, isPrerenderBuild } from '@/utils/prerender'

export default {
  name: 'PublicPage',
  mixins: [metaMixin],
  components: {
    RippedHeader
  },
  data() {
    return {
      loading: true,
      page: null,
      blockHeights: {},
    }
  },
  mounted() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.measureAllBlocks)
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.measureAllBlocks)
    }
  },
  async created() {
    await this.loadPage()
  },
  watch: {
    '$route.path': {
      handler() { this.loadPage() },
    }
  },
  methods: {
    async tryFindByPath(colName, variants) {
      const db = getFirestore()
      const col = collection(db, colName)
      for (const pv of variants) {
        const q = query(col, where('path', '==', pv), where('published', '==', true), limit(1))
        const snap = await getDocs(q)
        if (!snap.empty) {
          return snap.docs[0]
        }
      }
      return null
    },
    async loadPage() {
      this.loading = true
      this.page = null
      try {
        const variants = pathVariants(this.$route.path)

        // Try CMS Pages first
        const pageDoc = await this.tryFindByPath('pages', variants)
        if (pageDoc) {
          const data = pageDoc.data()
          this.page = { id: pageDoc.id, ...data, blocks: Array.isArray(data.blocks) ? data.blocks : [] }
          const title = data.metaTitle || data.title || 'Page'
          const description = data.metaDescription || ''
          this.updatePageMeta(title, description)
          return
        }

        // Fallback: Redirects
        const db = getFirestore()
        const redirectsCol = collection(db, 'redirects')
        for (const pv of variants) {
          const q = query(redirectsCol, where('from', '==', normalizeRedirectPath(pv)), where('published', '==', true), limit(1))
          const snap = await getDocs(q)
          if (!snap.empty) {
            const redirect = snap.docs[0].data()
            const redirectKey = redirect.from || normalizeRedirectPath(pv)
            // Prevent loops
            if (redirect.type === 'path' && typeof redirect.to === 'string') {
              if (normalizeRedirectPath(redirect.to) === normalizeRedirectPath(pv)) {
                break
              }
            }
            if (redirect.type === 'route' && typeof redirect.to === 'string') {
              const params = redirect.params && typeof redirect.params === 'object' ? redirect.params : undefined
              if (!isPrerenderBuild()) {
                this.logRedirectHit(redirectKey)
              }
              this.go(redirect.to, params)
              return
            }
            if (redirect.type === 'path' && typeof redirect.to === 'string') {
              if (!isPrerenderBuild()) {
                this.logRedirectHit(redirectKey)
              }
              this.go(redirect.to)
              return
            }
            if (redirect.type === 'external' && typeof redirect.to === 'string') {
              if (!isPrerenderBuild()) {
                this.logRedirectHit(redirectKey)
                this.go(redirect.to)
              }
              return
            }
          }
        }
        // If we get here, nothing matched. Log missing path and redirect to 404.
        if (!isPrerenderBuild()) {
          try {
            await this.logMissingPath()
          } catch (e) {
            // non-blocking
          }
        }
        // Redirect to the Lost (404) page
        this.$router.replace({ name: 'Lost' })
        return
      } catch (e) {
        console.error('Failed to load page', e)
        this.page = null
        // Redirect to the Lost (404) page on error
        this.$router.replace({ name: 'Lost' })
        return
      } finally {
        this.loading = false
        this.$nextTick(() => {
          this.measureAllBlocks()
          emitPrerenderReady()
        })
      }
    },
    measureAllBlocks() {
      if (!this.page || !Array.isArray(this.page.blocks)) return
      const heights = {}
      this.page.blocks.forEach(b => {
        const ref = this.$refs['textCol_' + b.id]
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el && el.$el) {
          heights[b.id] = el.$el.clientHeight
        } else if (el && el.clientHeight) {
          heights[b.id] = el.clientHeight
        }
      })
      this.blockHeights = heights
    },
    getImageStyle(blockId) {
      const h = this.blockHeights[blockId]
      if (!h) return {}
      return { maxHeight: h + 'px' }
    },
    onBlockImageLoad() {
      this.measureAllBlocks()
    },
    async logMissingPath() {
      try {
        const fullPath = this.$route.fullPath || this.$route.path || '/'
        const path = normalizeRedirectPath(this.$route.path || '/')
        const key = `missingPath:${fullPath}`
        if (sessionStorage.getItem(key)) return
        sessionStorage.setItem(key, '1')

        const db = getFirestore()
        const docId = encodeMissingPathId(path)
        const docRef = doc(db, 'missingPaths', docId)

        await setDoc(docRef, {
          path,
          hits: increment(1),
          lastSeen: serverTimestamp(),
          lastReferrer: typeof document !== 'undefined' ? document.referrer || '' : '',
          lastQuery: this.$route.query || {},
          lastHash: this.$route.hash || '',
          lastUserAgent: typeof navigator !== 'undefined' ? navigator.userAgent || '' : '',
        }, { merge: true })
      } catch (e) {
        // Swallow logging errors
      }
    },
    async logRedirectHit(fromPath) {
      try {
        const path = normalizeRedirectPath(fromPath || this.$route.path || '/')
        const db = getFirestore()
        const docRef = doc(db, 'redirectHits', encodeMissingPathId(path))
        await setDoc(docRef, {
          path,
          total: increment(1),
          lastUsed: serverTimestamp(),
          months: { [monthKey()]: increment(1) },
        }, { merge: true })
      } catch (e) {
        // Swallow logging errors
      }
    }
  }
}
</script> 

<style lang="scss">
  .page-content {
    h2 {
      color: #f26521 !important;
      // color: #8e2b16 !important;
      font-family: 'Barlow Condensed', sans-serif !important;
      font-size: 3rem !important;
    }
  }

@media (min-width: 960px) {
  .equal-height-row {
    align-items: stretch !important;
  }
  .image-col {
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  .image-wrapper {
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }
  .image-wrapper .block-image {
    max-height: 100%;
    max-width: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
  }
}
</style>